import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Check, ChevronLeft, Cpu, Shield, Monitor, Wifi, Zap, ShoppingBag, Bookmark } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import { useBag, setPendingBagItem } from '../../contexts/BagContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSaves } from '../../contexts/SavesContext';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const BuyArk = () => {
  const { region, formatPrice } = useRegion();
  const { addItem, processPendingItem } = useBag();
  const { isAuthenticated, isLoading } = useAuth();
  const { toggleSave, isSaved } = useSaves();
  const navigate = useNavigate();
  const [selectedMemory, setSelectedMemory] = useState('32gb');
  const [selectedStorage, setSelectedStorage] = useState('512gb');
  const [selectedConnectivity, setSelectedConnectivity] = useState('wifi');
  const [currentImage, setCurrentImage] = useState(0);
  const [showAddedConfirm, setShowAddedConfirm] = useState(false);

  // Auto-add pending item after auth redirect back
  useEffect(() => {
    if (isAuthenticated) {
      const wasProcessed = processPendingItem();
      if (wasProcessed) {
        setShowAddedConfirm(true);
        setTimeout(() => setShowAddedConfirm(false), 4000);
      }
    }
  }, [isAuthenticated, processPendingItem]);

  const basePrice = region.ark.min;
  const priceAdds = {
    memory: { '32gb': 0, '64gb': Math.round((region.ark.max - region.ark.min) * 0.25) },
    storage: { '512gb': 0, '1tb': Math.round((region.ark.max - region.ark.min) * 0.20), '2tb': Math.round((region.ark.max - region.ark.min) * 0.40) },
    connectivity: { 'wifi': 0, '10gbe': Math.round((region.ark.max - region.ark.min) * 0.15) },
  };

  const totalPrice = basePrice + priceAdds.memory[selectedMemory] + priceAdds.storage[selectedStorage] + priceAdds.connectivity[selectedConnectivity];

  const configs = {
    memory: [
      { id: '32gb', label: '32GB Unified Memory', note: 'Included', addPrice: priceAdds.memory['32gb'] },
      { id: '64gb', label: '64GB Unified Memory', note: 'Recommended for large modules', addPrice: priceAdds.memory['64gb'] },
    ],
    storage: [
      { id: '512gb', label: '512GB SSD', note: 'Included', addPrice: priceAdds.storage['512gb'] },
      { id: '1tb', label: '1TB SSD', note: 'Best for multiple exhibit modules', addPrice: priceAdds.storage['1tb'] },
      { id: '2tb', label: '2TB SSD', note: 'Full museum library', addPrice: priceAdds.storage['2tb'] },
    ],
    connectivity: [
      { id: 'wifi', label: 'Wi-Fi 6E + Bluetooth 5.3', note: 'Included', addPrice: priceAdds.connectivity['wifi'] },
      { id: '10gbe', label: '10 Gigabit Ethernet', note: 'For enterprise backbone', addPrice: priceAdds.connectivity['10gbe'] },
    ],
  };

  const images = [
    process.env.PUBLIC_URL + '/ark-product.png',
    process.env.PUBLIC_URL + '/fozzil-intelligence-core.png',
  ];

  const handleAddToBag = () => {
    const bagItem = {
      id: 'fozzil-ark',
      name: 'FOZZIL ARK',
      tagline: 'Intelligence. Contained.',
      image: images[0],
      config: [
        { label: 'Chip', value: 'T.A.E.L.S Core' },
        { label: 'Memory', value: configs.memory.find(m => m.id === selectedMemory)?.label },
        { label: 'Storage', value: configs.storage.find(s => s.id === selectedStorage)?.label },
        { label: 'Connectivity', value: configs.connectivity.find(c => c.id === selectedConnectivity)?.label },
      ],
      price: totalPrice,
    };

    if (!isAuthenticated) {
      if (isLoading) return; // Wait if still checking session
      // Store the item so it auto-adds after sign-in
      setPendingBagItem(bagItem);
      navigate('/auth', { state: { from: { pathname: '/shop/buy-ark/ark' } } });
      return;
    }
    addItem(bagItem);
    setShowAddedConfirm(true);
    setTimeout(() => setShowAddedConfirm(false), 4000);
  };

  const handleToggleSave = () => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { from: { pathname: '/shop/buy-ark/ark' } } });
      return;
    }
    
    const product = {
      id: 'fozzil-ark',
      name: 'FOZZIL ARK',
      tagline: 'Intelligence. Contained.',
      image: images[0],
      link: '/shop/buy-ark/ark',
      price: formatPrice(totalPrice),
    };
    
    toggleSave(product);
  };

  const ConfigSection = ({ title, options, selected, onSelect }) => (
    <div className="mb-8">
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3">{title}</h3>
      <div className="flex flex-col gap-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className={`w-full text-left rounded-[32px] p-4 border-2 transition-all duration-200 cursor-pointer bg-transparent ${
              selected === opt.id
                ? 'border-[#065DC0] bg-[#065DC0]/[0.06]'
                : 'border-black/[0.08] dark:border-white/[0.08] hover:border-black/[0.18] dark:hover:border-white/[0.18]'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{opt.label}</p>
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-0.5">{opt.note}</p>
              </div>
              <div className="flex items-center gap-2">
                {opt.addPrice > 0 && (
                  <span className="text-xs text-[#6e6e73] dark:text-[#a1a1a6] font-medium">+{formatPrice(opt.addPrice)}</span>
                )}
                {selected === opt.id && <Check size={18} className="text-[#065DC0] shrink-0" />}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const checkoutConfig = [
    ['Chip', 'T.A.E.L.S Core'],
    ['Memory', configs.memory.find(m => m.id === selectedMemory)?.label],
    ['Storage', configs.storage.find(s => s.id === selectedStorage)?.label],
    ['Connectivity', configs.connectivity.find(c => c.id === selectedConnectivity)?.label],
  ];

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
      {/* Added to Bag Toast */}
      <AnimatePresence>
        {showAddedConfirm && (
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-[3000] bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] px-6 py-4 rounded-[32px] shadow-2xl flex items-center gap-4 max-w-[420px] w-[90vw]"
          >
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
              <Check size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Added to Bag</p>
              <p className="text-xs opacity-70 truncate">FOZZIL ARK — {formatPrice(totalPrice)}</p>
            </div>
            <button
              onClick={() => { setShowAddedConfirm(false); navigate('/shop/bag'); }}
              className="px-4 py-2 rounded-full bg-[#065DC0] text-white text-xs font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all shrink-0"
            >
              Review Bag
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breadcrumb */}
      <div className="max-w-[1120px] mx-auto px-6 py-4">
        <Link to="/shop/buy-ark" className="inline-flex items-center gap-1 text-[#065DC0] text-sm font-medium hover:underline">
          <ChevronLeft size={16} /> Back to Shop ARK
        </Link>
      </div>

      {/* Product Section */}
      <div className="max-w-[1120px] mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Image Gallery */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:sticky lg:top-[120px] self-start">
            <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-[32px] border border-black/[0.08] dark:border-white/[0.08] flex items-center justify-center aspect-square overflow-hidden transition-colors duration-400">
              <img src={images[currentImage]} alt="FOZZIL ARK" className="w-full h-full object-contain transition-all duration-500" />
            </div>
            <div className="flex items-center justify-center gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`w-16 h-16 rounded-[32px] border overflow-hidden cursor-pointer transition-all duration-200 p-1 ${
                    currentImage === i
                      ? 'border-[#065DC0] shadow-[0_0_0_2px_rgba(6,93,192,0.3)]'
                      : 'border-black/[0.08] dark:border-white/[0.08] hover:border-black/[0.18] dark:hover:border-white/[0.18]'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Right: Configuration */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="py-4">
            <span className="text-[11px] font-bold text-[#bf4800] uppercase tracking-wide">NEW</span>
            <h1 className="text-[clamp(32px,4vw,48px)] font-bold tracking-tight leading-tight mt-1 mb-2">FOZZIL ARK</h1>
            <p className="text-lg text-[#6e6e73] dark:text-[#a1a1a6] mb-2">Intelligence. Contained.</p>
            <p className="text-2xl font-semibold mb-1">From {formatPrice(region.ark.min)}</p>
            <p className="text-sm text-[#86868b] dark:text-[#6e6e73] mb-8">{formatPrice(region.ark.min)} – {formatPrice(region.ark.max)}</p>

            {/* Chip - Fixed */}
            <div className="mb-8">
              <h3 className="text-sm font-bold uppercase tracking-wide mb-3">Chip</h3>
              <div className="border-2 border-[#065DC0] rounded-[32px] p-4 bg-[#065DC0]/[0.06] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cpu size={20} className="text-[#065DC0]" />
                    <div>
                      <p className="text-sm font-semibold">T.A.E.L.S Core</p>
                      <p className="text-xs text-[#86868b] dark:text-[#6e6e73]">18 trillion operations per second</p>
                    </div>
                  </div>
                  <Check size={18} className="text-[#065DC0]" />
                </div>
              </div>
            </div>

            <ConfigSection title="Memory" options={configs.memory} selected={selectedMemory} onSelect={setSelectedMemory} />
            <ConfigSection title="Storage" options={configs.storage} selected={selectedStorage} onSelect={setSelectedStorage} />
            <ConfigSection title="Connectivity" options={configs.connectivity} selected={selectedConnectivity} onSelect={setSelectedConnectivity} />

            {/* Summary */}
            <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-[32px] border border-black/[0.08] dark:border-white/[0.08] p-6 mb-8 transition-colors duration-400">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold">Your FOZZIL ARK</h3>
                <span className="text-lg font-bold text-[#065DC0]">{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex flex-col gap-2.5 text-sm">
                {checkoutConfig.map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-[#6e6e73] dark:text-[#a1a1a6]">{label}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 mt-1 border-t border-black/[0.08] dark:border-white/[0.08]">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-[#065DC0]">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* CTA — Add to Bag & Save */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <button
                  onClick={handleToggleSave}
                  className={`flex-1 px-4 py-3.5 rounded-full bg-transparent text-[#065DC0] text-sm font-semibold border border-[#065DC0] text-center inline-flex items-center justify-center gap-2 hover:bg-[#065DC0] hover:text-white transition-all cursor-pointer ${isSaved('fozzil-ark') ? 'bg-[#065DC0] !text-white' : ''}`}
                >
                  <Bookmark size={18} fill={isSaved('fozzil-ark') ? 'currentColor' : 'none'} /> {isSaved('fozzil-ark') ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={handleAddToBag}
                  className="flex-[2] px-6 py-3.5 rounded-full bg-[#065DC0] text-white text-sm font-semibold text-center inline-flex items-center justify-center gap-2 hover:bg-[#0077ed] hover:scale-[1.01] transition-all cursor-pointer border-none"
                >
                  <ShoppingBag size={18} /> Add to Bag — {formatPrice(totalPrice)}
                </button>
              </div>
              <Link to="/fozzil-ark" target="_blank" rel="noopener noreferrer" className="w-full px-8 py-3.5 mx-auto rounded-full bg-transparent text-[#065DC0] text-sm font-semibold border border-[#065DC0] text-center inline-flex items-center justify-center gap-2 hover:bg-[#065DC0] hover:text-white transition-all">
                Learn more about ARK
              </Link>
            </div>

            {/* What's Included */}
            <div className="mt-10 border-t border-black/[0.08] dark:border-white/[0.08] pt-8">
              <h3 className="text-base font-bold mb-5">What's included</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Shield size={20} />, label: 'S.H.A.D.E. Vision' },
                  { icon: <Cpu size={20} />, label: 'Intelligence Core' },
                  { icon: <Monitor size={20} />, label: '8K HDMI 2.1 Output' },
                  { icon: <Wifi size={20} />, label: 'Wi-Fi 6E' },
                  { icon: <Zap size={20} />, label: 'Living Ancestor Engine' },
                  { icon: <Shield size={20} />, label: 'Neural Vault' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5">
                    <div className="text-[#065DC0]">{item.icon}</div>
                    <span className="text-sm text-[#6e6e73] dark:text-[#a1a1a6] font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BuyArk;
